import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { enqueue as enqueueOffline, enqueueAction } from "@/lib/offline";
import { cacheGet } from "@/lib/offlineCache";
import TagScanDialog from "@/components/TagScanDialog";
import { AnimalSubform } from "@/components/registration/AnimalSubform";
import { RegistrationPreview } from "@/components/registration/RegistrationPreview";
import { RegistrationSuccess } from "@/components/registration/RegistrationSuccess";
import HelpVideoButton from "@/components/HelpVideoButton";
import DashboardShell from "@/components/layout/DashboardShell";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Wallet, User, Beef, Truck, Search, X, CheckCircle2,
  Eye, Minus, Plus,
} from "lucide-react";

const DRAFT_KEY = "am_new_reg_draft";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const ANIMAL_TYPES = ["buffalo", "cow", "goat", "sheep", "horse"];
const ID_MARKS = ["White Spot", "Black Spot", "Broken Horn", "Tail Cut", "Ear Cut", "No Mark", "Other"];
const MAX_ANIMALS = 10;
const EMPTY_ANIMAL = {
  animal_type: "", breed: "", breed_custom: "", gender: "female", sale_price: "",
  age_years: "", age_months: "", weight_kg: "", colour: "", colour_custom: "",
  identification_mark: "", identification_custom: "", ear_tag: "", photos: [],
};
// Each animal row gets a stable client-side uid so React keys survive list edits.
const newAnimal = () => ({ ...EMPTY_ANIMAL, _uid: Math.random().toString(36).slice(2, 10) });

const PURPOSES = [
  { v: "entry_fee", label: "Entry Fee" },
  { v: "animal_registration", label: "Animal Registration" },
  { v: "parking", label: "Parking" },
  { v: "other", label: "Other" },
];

const EMPTY = {
  payment: { amount: "", payment_type: "cash", purpose: "animal_registration" },
  seller: { name: "", mobile: "", govt_id: "", address: "" },
  purchaser_available: false,
  purchaser: {
    name: "", mobile: "", govt_id: "", address: "",
    village: "", tehsil: "", district: "", state: "", pin_code: "",
  },
  animals: [newAnimal()],
  vehicle: { vehicle_number: "", driver_name: "", driver_mobile: "" },
};

function loadDraft() {
  try {
    const s = localStorage.getItem(DRAFT_KEY);
    if (!s) return EMPTY;
    const parsed = JSON.parse(s);
    // Expire drafts older than TTL — PII hygiene
    if (parsed?._savedAt && Date.now() - parsed._savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return EMPTY;
    }
    const { _savedAt, ...clean } = parsed;
    // Migrate old single-animal drafts to the quantity-based array shape.
    if (clean.animal && !clean.animals) {
      clean.animals = [{ ...EMPTY_ANIMAL, ...clean.animal }];
      delete clean.animal;
    }
    if (!Array.isArray(clean.animals) || clean.animals.length === 0) clean.animals = [newAnimal()];
    clean.animals = clean.animals.map((a) => (a._uid ? a : { ...a, _uid: Math.random().toString(36).slice(2, 10) }));
    return { ...EMPTY, ...clean };
  } catch (err) { console.debug("draft load failed", err); }
  return EMPTY;
}

const persistDraft = (data) => {
  localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...data, _savedAt: Date.now() }));
};

function Section({ n, icon: Icon, title, children, done, help }) {
  return (
    <section className="am-card p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 rounded-2xl grid place-items-center ${done ? "bg-primary text-primary-foreground" : "bg-accent text-primary"}`}>
          {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Section {n}</div>
          <h2 className="font-display font-bold text-lg leading-tight">{title}</h2>
        </div>
        {help}
      </div>
      {children}
    </section>
  );
}

export default function HelperNewRegistration() {
  const { t } = useT();
  const nav = useNavigate();
  const user = getUser() || {};
  const [form, setForm] = useState(loadDraft);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [master, setMaster] = useState([]);
  const [scanIdx, setScanIdx] = useState(null);

  // Master data (breeds / colours / default gender) — served from the encrypted
  // offline cache when the device has no connectivity.
  useEffect(() => {
    api.get("/master/animals").then((r) => setMaster(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const cfgFor = (type) => master.find((m) => m.animal_type === type);
  const enabledNames = (cfg, key) => (cfg?.[key] || []).filter((x) => x.enabled).map((x) => x.name);

  // Owner-configured ₹/animal registration prices (read-only for helpers).
  // api.get falls back to the encrypted offline cache, so fees compute offline too.
  const [regPrices, setRegPrices] = useState(null);
  useEffect(() => {
    api.get("/helper/registration-prices").then((r) => setRegPrices(r.data.prices || {})).catch(() => setRegPrices({}));
  }, []);

  // SERVER-derived receipt identity (haat/owner/helper names). api.get serves
  // the encrypted offline cache when offline, so offline slips show them too.
  const [receiptCtx, setReceiptCtx] = useState({});
  useEffect(() => {
    api.get("/helper/receipt-context").then((r) => setReceiptCtx(r.data || {})).catch(() => {});
  }, []);

  // Auto fee mode: registration purpose + every selected type has an owner price.
  const selectedTypes = form.animals.map((a) => a.animal_type).filter(Boolean);
  const autoFee = form.payment.purpose === "animal_registration"
    && !!regPrices && Object.keys(regPrices).length > 0
    && selectedTypes.length > 0
    && selectedTypes.every((tp) => Number(regPrices[tp]) > 0);

  // Per-type breakdown: quantity × owner price (helper cannot edit).
  const feeBreakdown = (() => {
    if (!autoFee) return [];
    const byType = {};
    for (const tp of selectedTypes) {
      byType[tp] = byType[tp] || { animal_type: tp, quantity: 0, price_per_animal: Number(regPrices[tp]), total: 0 };
      byType[tp].quantity += 1;
      byType[tp].total = byType[tp].quantity * byType[tp].price_per_animal;
    }
    return Object.values(byType);
  })();
  const feeTotal = feeBreakdown.reduce((s, l) => s + l.total, 0);

  // Keep payment.amount locked to the computed grand total while auto mode is on.
  useEffect(() => {
    if (autoFee && String(form.payment.amount) !== String(feeTotal)) {
      setForm((f) => {
        const next = { ...f, payment: { ...f.payment, amount: String(feeTotal) } };
        persistDraft(next);
        return next;
      });
    }
  }, [autoFee, feeTotal, form.payment.amount]);

  // Seller & Purchaser are optional. Only validate format IF a mobile is provided.
  const submitDisabled = busy;

  const set = (path, value) => {
    setForm((f) => {
      const next = { ...f, [path]: value };
      persistDraft(next);
      return next;
    });
  };
  const patch = (section, key, value) => {
    setForm((f) => {
      const next = { ...f, [section]: { ...f[section], [key]: value } };
      persistDraft(next);
      return next;
    });
  };

  const patchAnimal = (idx, key, value) => {
    setForm((f) => {
      const animals = f.animals.map((a, i) => (i === idx ? { ...a, [key]: value } : a));
      const next = { ...f, animals };
      persistDraft(next);
      return next;
    });
  };

  // Smart defaults — picking a type applies the master default gender and resets breed/colour.
  const onTypeChange = (idx, v) => {
    const cfg = cfgFor(v);
    setForm((f) => {
      const animals = f.animals.map((a, i) => (i === idx
        ? { ...a, animal_type: v, gender: cfg?.default_gender || "female", breed: "", breed_custom: "", colour: "", colour_custom: "" }
        : a));
      const next = { ...f, animals };
      persistDraft(next);
      return next;
    });
  };

  // Quantity-based dynamic form — N animals, each with separate details.
  const setQuantity = (nRaw) => {
    const n = Math.max(1, Math.min(MAX_ANIMALS, Number(nRaw) || 1));
    setQtyText(String(n));
    setForm((f) => {
      const animals = f.animals.slice(0, n);
      while (animals.length < n) animals.push(newAnimal());
      const next = { ...f, animals };
      persistDraft(next);
      return next;
    });
  };

  // Editable text state so the field can be cleared while typing (fixes the
  // "1 → type 2 → becomes 12 → clamped to 10" jump). Applied on valid input/blur.
  const [qtyText, setQtyText] = useState(String(form.animals.length));
  useEffect(() => { setQtyText(String(form.animals.length)); }, [form.animals.length]);

  const onQtyChange = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 2);
    setQtyText(digits);
    const n = Number(digits);
    if (digits !== "" && n >= 1 && n <= MAX_ANIMALS) setQuantity(n);
  };
  const onQtyBlur = () => setQuantity(qtyText);

  const searchSellerByMobile = async () => {
    const m = form.seller.mobile.trim();
    if (!/^\d{6,15}$/.test(m)) { toast.error("Enter a valid mobile first"); return; }
    try {
      const r = await api.get(`/helper/sellers/by-mobile/${m}`);
      setForm((f) => ({ ...f, seller: { name: r.data.name, mobile: r.data.mobile, govt_id: r.data.govt_id || "", address: r.data.address || "" } }));
      toast.success(`Found: ${r.data.name}`);
    } catch (e) {
      if (!e?.response) {
        // Offline — search the locally cached seller list instead.
        const cached = await cacheGet("/helper/sellers");
        const hit = Array.isArray(cached) ? cached.find((s) => s.mobile === m) : null;
        if (hit) {
          setForm((f) => ({ ...f, seller: { name: hit.name, mobile: hit.mobile, govt_id: hit.govt_id || "", address: hit.address || "" } }));
          toast.success(`Found (offline): ${hit.name}`);
        } else toast.info("Offline — no local match, fill details below");
        return;
      }
      if (e?.response?.status === 404) toast.info("New seller — fill details below");
      else toast.error("Search failed");
    }
  };

  const searchPurchaserByMobile = async () => {
    const m = form.purchaser.mobile.trim();
    if (!/^\d{6,15}$/.test(m)) { toast.error("Enter a valid mobile first"); return; }
    try {
      const r = await api.get(`/helper/purchasers/by-mobile/${m}`);
      setForm((f) => ({
        ...f,
        purchaser: {
          name: r.data.name || "",
          mobile: r.data.mobile || "",
          govt_id: r.data.govt_id || "",
          address: r.data.address || "",
          village: r.data.village || "",
          tehsil: r.data.tehsil || "",
          district: r.data.district || "",
          state: r.data.state || "",
          pin_code: r.data.pin_code || "",
        },
      }));
      toast.success(`Found: ${r.data.name}`);
    } catch (e) {
      if (!e?.response) {
        // Offline — search the locally cached purchaser list instead.
        const cached = await cacheGet("/helper/purchasers");
        const hit = Array.isArray(cached) ? cached.find((p) => p.mobile === m) : null;
        if (hit) {
          setForm((f) => ({
            ...f,
            purchaser: {
              name: hit.name || "", mobile: hit.mobile || "", govt_id: hit.govt_id || "", address: hit.address || "",
              village: hit.village || "", tehsil: hit.tehsil || "", district: hit.district || "", state: hit.state || "", pin_code: hit.pin_code || "",
            },
          }));
          toast.success(`Found (offline): ${hit.name}`);
        } else toast.info("Offline — no local match, fill details below");
        return;
      }
      if (e?.response?.status === 404) toast.info("New purchaser — fill details below");
      else toast.error("Search failed");
    }
  };

  /* Photo capture UI removed for faster field registration — the photo
     architecture (animal.photos schema, /registration/{id}/photos endpoint,
     offline photo queue below) is kept for a future optional feature. */

  const submit = async () => {
    // Payment & animal type remain the only hard requirements (business logic).
    if (!form.payment.amount || Number(form.payment.amount) <= 0) { toast.error("Enter a payment amount"); return; }
    if (form.seller.mobile && !/^\d{6,15}$/.test(form.seller.mobile)) { toast.error("Invalid seller mobile"); return; }
    if (form.purchaser_available && form.purchaser.mobile && !/^\d{6,15}$/.test(form.purchaser.mobile)) { toast.error("Invalid purchaser mobile"); return; }
    for (let i = 0; i < form.animals.length; i++) {
      const a = form.animals[i];
      const tag = form.animals.length > 1 ? `Animal ${i + 1}: ` : "";
      if (!a.animal_type) { toast.error(`${tag}pick animal type`); return; }
      if (a.weight_kg !== "" && (isNaN(Number(a.weight_kg)) || Number(a.weight_kg) <= 0)) { toast.error(`${tag}weight must be a positive number (kg)`); return; }
    }

    setBusy(true);
    try {
      // Resolve "Other" selections to their free-text values before submitting.
      const finalOf = (a) => ({
        animal_type: a.animal_type,
        breed: a.breed === "Other" ? (a.breed_custom?.trim() || "Other") : a.breed,
        gender: a.gender || "female",
        count: 1,
        sale_price: Number(String(a.sale_price).replace(/[^\d.]/g, "") || 0),
        age_years: Number(a.age_years || 0),
        age_months: Number(a.age_months || 0),
        weight_kg: Number(a.weight_kg || 0),
        colour: a.colour === "Other" ? (a.colour_custom?.trim() || "Other") : a.colour,
        identification_mark: a.identification_mark === "Other" ? (a.identification_custom?.trim() || "Other") : a.identification_mark,
        ear_tag: a.ear_tag,
        photos: a.photos,
      });
      const animals = form.animals.map(finalOf);
      const payload = {
        payment: {
          amount: Number(form.payment.amount),
          payment_type: form.payment.payment_type,
          purpose: form.payment.purpose,
        },
        seller: form.seller,
        purchaser: form.purchaser_available ? form.purchaser : null,
        animal: animals[0],
        animals,
        vehicle: form.vehicle,
        entry_gate: "MAIN",
      };

      // Offline-first: if the device is offline OR the request fails with a
      // network error, we queue the payload in IndexedDB and auto-sync when
      // connectivity returns. NEVER lose an offline capture.
      const queueLocally = async (reason) => {
        // Lightweight sync: the registration JSON (receipt data) is queued WITHOUT
        // photos so each sync request is a few KB. Photos are queued as separate
        // follow-up actions that replay after the registration (queue is ordered).
        const lean = {
          ...payload,
          animal: { ...payload.animal, photos: [] },
          animals: payload.animals.map((sp) => ({ ...sp, photos: [] })),
        };
        const item = await enqueueOffline(lean, { created_by: user?.user_id, entity: "registration" });
        for (let i = 0; i < payload.animals.length; i++) {
          const ph = payload.animals[i].photos || [];
          if (ph.length) {
            await enqueueAction(`/helper/registration/${item.client_id}/photos`, undefined,
              { entity: "photos", created_by: user?.user_id, body: { animal_index: i, photos: ph } });
          }
        }
        toast.success(`Saved offline · will sync when online (${reason})`);
        setPreviewOpen(false);
        setResult({
          offline: true,
          animal: { registration_number: `PENDING-${item.client_id.slice(-6).toUpperCase()}` },
          receipt: { receipt_number: `PENDING`, amount: payload.payment.amount, fee_breakdown: autoFee ? feeBreakdown : null,
                     haat_name: receiptCtx.haat_name, owner_name: receiptCtx.owner_name,
                     helper_name: receiptCtx.helper_name || user?.name },
          gatepass: { pass_number: `PENDING`, qr_payload: item.client_id },
        });
        localStorage.removeItem(DRAFT_KEY);
      };

      if (!navigator.onLine) {
        await queueLocally("offline");
      } else {
        try {
          const r = await api.post("/helper/registration", payload);
          setPreviewOpen(false);
          setResult(r.data);
          localStorage.removeItem(DRAFT_KEY);
          toast.success(`Registration complete · ${r.data.animal.registration_number}`);
        } catch (e) {
          // Distinguish network failure (queue) from real validation errors (surface).
          const isNetwork = !e?.response || e?.code === "ERR_NETWORK";
          if (isNetwork) {
            await queueLocally("network error");
          } else {
            toast.error(e?.response?.data?.detail || "Failed to complete registration");
          }
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const clearDraft = () => {
    if (!window.confirm("Discard the draft and start over?")) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm(EMPTY);
  };

  return (
    <DashboardShell
      title="New Animal Registration"
      subtitle="One flow · Payment → Seller → Purchaser → Animal → Vehicle → Gate Pass"
      action={
        <Button variant="outline" className="rounded-full" onClick={clearDraft} data-testid="new-reg-clear">
          <X className="w-4 h-4 mr-1" /> Clear draft
        </Button>
      }
    >
      <div className="space-y-5 max-w-3xl mx-auto pb-24">

        {/* Section 1 - Payment */}
        <Section n={1} icon={Wallet} title="Payment Receipt" done={Number(form.payment.amount) > 0} help={<HelpVideoButton screen="payment_receipt" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Amount (₹)</label>
              <Input
                data-testid="new-reg-amount"
                type="number"
                inputMode="numeric"
                className={`am-input mt-1 text-3xl font-display font-extrabold py-5 ${autoFee ? "bg-muted cursor-not-allowed" : ""}`}
                value={form.payment.amount}
                readOnly={autoFee}
                onChange={(e) => { if (!autoFee) patch("payment", "amount", e.target.value); }}
                placeholder="0"
              />
              {autoFee && (
                <div className="mt-2 rounded-2xl border bg-accent/40 p-3" data-testid="reg-fee-breakdown">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                    Auto-calculated · prices set by Haat owner
                  </div>
                  {feeBreakdown.map((l) => (
                    <div key={l.animal_type} className="flex justify-between text-sm py-0.5" data-testid={`fee-line-${l.animal_type}`}>
                      <span className="capitalize">{l.animal_type} × {l.quantity} @ ₹{l.price_per_animal.toLocaleString("en-IN")}</span>
                      <span className="font-bold">₹ {l.total.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm border-t mt-1.5 pt-1.5 font-extrabold" data-testid="reg-fee-total">
                    <span>Grand Total</span>
                    <span>₹ {feeTotal.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Payment Type</label>
              <Select value={form.payment.payment_type} onValueChange={(v) => patch("payment", "payment_type", v)}>
                <SelectTrigger className="am-input mt-1" data-testid="new-reg-pay-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Purpose</label>
              <Select value={form.payment.purpose} onValueChange={(v) => patch("payment", "purpose", v)}>
                <SelectTrigger className="am-input mt-1" data-testid="new-reg-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PURPOSES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 text-xs text-muted-foreground">
              Receipt number & timestamp are auto-generated on save.
            </div>
          </div>
        </Section>

        {/* Section 2 - Seller & Purchaser (merged) */}
        <Section
          n={2}
          icon={User}
          title="Seller & Purchaser Details"
          done={!!((form.seller.name || form.seller.mobile) || (form.purchaser_available && (form.purchaser.name || form.purchaser.mobile)))}
          help={<HelpVideoButton screen="seller_registration" />}
        >
          {/* --- Seller sub-block --- */}
          <div className="mb-2">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Seller</div>
            <div className="grid grid-cols-1 gap-3">
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Input
                  data-testid="new-reg-seller-mobile"
                  placeholder="Mobile number"
                  inputMode="numeric"
                  className="am-input"
                  value={form.seller.mobile}
                  onChange={(e) => patch("seller", "mobile", e.target.value.replace(/\D/g, ""))}
                />
                <Button variant="outline" className="rounded-full" onClick={searchSellerByMobile} data-testid="new-reg-seller-search">
                  <Search className="w-4 h-4 mr-1" /> Search
                </Button>
              </div>
              <Input data-testid="new-reg-seller-name" placeholder="Full name" className="am-input" value={form.seller.name} onChange={(e) => patch("seller", "name", e.target.value)} />
              <Input placeholder="Aadhaar number (optional)" inputMode="numeric" maxLength={12} className="am-input" value={form.seller.govt_id} onChange={(e) => patch("seller", "govt_id", e.target.value.replace(/\D/g, "").slice(0, 12))} />
              <Input placeholder="Address" className="am-input" value={form.seller.address} onChange={(e) => patch("seller", "address", e.target.value)} />
            </div>
          </div>

          {/* --- Divider + Purchaser sub-block --- */}
          <div className="border-t my-4" />
          <div>
            <div className="flex items-center justify-between mb-2 gap-3">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Purchaser</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Purchaser available</span>
                <Switch
                  data-testid="new-reg-purchaser-toggle"
                  checked={form.purchaser_available}
                  onCheckedChange={(v) => set("purchaser_available", v)}
                />
              </div>
            </div>
            {form.purchaser_available && (
              <div className="grid grid-cols-1 gap-3">
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <Input
                    data-testid="new-reg-purchaser-mobile"
                    placeholder="Mobile number"
                    inputMode="numeric"
                    maxLength={15}
                    className="am-input"
                    value={form.purchaser.mobile}
                    onChange={(e) => patch("purchaser", "mobile", e.target.value.replace(/\D/g, ""))}
                  />
                  <Button variant="outline" className="rounded-full" onClick={searchPurchaserByMobile} data-testid="new-reg-purchaser-search">
                    <Search className="w-4 h-4 mr-1" /> Search
                  </Button>
                </div>
                <Input
                  data-testid="new-reg-purchaser-name"
                  placeholder="Purchaser name"
                  className="am-input"
                  value={form.purchaser.name}
                  onChange={(e) => patch("purchaser", "name", e.target.value)}
                />
                <Input
                  data-testid="new-reg-purchaser-aadhaar"
                  placeholder="Aadhaar number (optional)"
                  inputMode="numeric"
                  maxLength={12}
                  className="am-input"
                  value={form.purchaser.govt_id}
                  onChange={(e) => patch("purchaser", "govt_id", e.target.value.replace(/\D/g, "").slice(0, 12))}
                />
                <Input
                  data-testid="new-reg-purchaser-address"
                  placeholder="Address"
                  className="am-input"
                  value={form.purchaser.address}
                  onChange={(e) => patch("purchaser", "address", e.target.value)}
                />
              </div>
            )}
          </div>
        </Section>

        {/* Section 4 - Animal (Smart Registration: quantity-based, master-data driven) */}
        <Section n={4} icon={Beef} title="Animal Details" done={form.animals.every((a) => !!a.animal_type)} help={<HelpVideoButton screen="animal_registration" />}>
          <div className="mb-4 max-w-[14rem]">
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Quantity (Animals)</label>
            <div className="mt-1 flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" data-testid="new-reg-qty-minus"
                aria-label="Decrease quantity" className="rounded-full h-9 w-9 shrink-0"
                disabled={form.animals.length <= 1}
                onClick={() => setQuantity(form.animals.length - 1)}>
                <Minus className="w-4 h-4" />
              </Button>
              <Input data-testid="new-reg-quantity" type="number" min={1} max={MAX_ANIMALS} step={1} inputMode="numeric"
                className="am-input text-center" value={qtyText}
                onChange={(e) => onQtyChange(e.target.value)} onBlur={onQtyBlur} />
              <Button type="button" variant="outline" size="icon" data-testid="new-reg-qty-plus"
                aria-label="Increase quantity" className="rounded-full h-9 w-9 shrink-0"
                disabled={form.animals.length >= MAX_ANIMALS}
                onClick={() => setQuantity(form.animals.length + 1)}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-5">
            {form.animals.map((a, idx) => (
              <AnimalSubform
                key={a._uid || idx}
                a={a}
                idx={idx}
                showTitle={form.animals.length > 1}
                typeOptions={master.length ? master.map((m) => m.animal_type) : ANIMAL_TYPES}
                breeds={enabledNames(cfgFor(a.animal_type), "breeds")}
                colours={enabledNames(cfgFor(a.animal_type), "colours")}
                onTypeChange={onTypeChange}
                patchAnimal={patchAnimal}
                onScanRequest={setScanIdx}
              />
            ))}
          </div>
        </Section>

        {/* Section 5 - Vehicle */}
        <Section n={5} icon={Truck} title="Vehicle Details">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input placeholder="Vehicle number" className="am-input" value={form.vehicle.vehicle_number} onChange={(e) => patch("vehicle", "vehicle_number", e.target.value)} />
            <Input placeholder="Driver name" className="am-input" value={form.vehicle.driver_name} onChange={(e) => patch("vehicle", "driver_name", e.target.value)} />
            <Input placeholder="Driver mobile" inputMode="numeric" className="am-input" value={form.vehicle.driver_mobile} onChange={(e) => patch("vehicle", "driver_mobile", e.target.value.replace(/\D/g, ""))} />
          </div>
        </Section>

        {/* Section 6 - Preview & Save */}
        <Section n={6} icon={CheckCircle2} title="Preview & Save" help={<HelpVideoButton screen="entry_pass" />}>
          <div className="text-sm text-muted-foreground">
            Review your entries. When you save, AnimalMela atomically generates:
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Registration Slip</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> QR Gate Pass</li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Receipt (PDF)</li>
          </ul>
        </Section>

        {/* Sticky submit bar (mobile) */}
        <div className="fixed bottom-0 left-0 right-0 sm:relative bg-background/95 backdrop-blur border-t sm:border-0 p-4 sm:p-0 z-20">
          <div className="max-w-3xl mx-auto flex gap-2">
            <Button
              data-testid="new-reg-preview"
              className="am-btn-primary flex-1 py-6 text-base"
              onClick={() => setPreviewOpen(true)}
              disabled={busy}
            >
              <Eye className="w-4 h-4 mr-2" /> Preview &amp; Save
            </Button>
          </div>
        </div>
      </div>

      <RegistrationPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        form={form}
        busy={busy}
        onCancel={() => {
          setPreviewOpen(false);
          setForm(EMPTY);
          try { localStorage.removeItem(DRAFT_KEY); } catch (err) { console.debug("draft clear failed", err); }
        }}
        onSubmit={submit}
      />

      <RegistrationSuccess
        result={result}
        form={form}
        user={user}
        onOpenChange={(o) => { if (!o) { setResult(null); nav("/helper"); } }}
        onAnother={() => { setResult(null); setForm(EMPTY); }}
        onDone={() => { setResult(null); nav("/helper"); }}
      />

      {/* Ear-tag QR scanner (offline capable) */}
      <TagScanDialog
        open={scanIdx !== null}
        onOpenChange={(o) => { if (!o) setScanIdx(null); }}
        onScan={(text) => { if (scanIdx !== null && text) patchAnimal(scanIdx, "ear_tag", text); }}
      />
    </DashboardShell>
  );
}

